import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  ShoppingCart,
  AlertTriangle,
  Lightbulb,
  Megaphone,
  Settings2,
  Zap,
  GraduationCap,
  Plus,
  Loader2,
  Calendar,
  User,
  MapPin,
  Image,
  FileText,
  Clock,
  CheckCircle2,
  XCircle,
  Phone,
  Mail,
  Building2,
  Eye,
  EyeOff,
  Trash2,
  Edit,
  ChevronDown,
  ChevronRight,
  UserCheck,
  UserX,
  Upload,
  Receipt,
  Camera,
  Wifi,
  WifiOff,
  Flame,
  Droplets,
  Shield,
  Wrench,
  Home,
  History,
  Euro,
  TrendingUp,
  Store,
  ScanLine,
  UserPlus,
  Briefcase,
  CalendarClock,
  ClipboardCheck,
  Download,
  Search,
  BarChart3,
  Filter,
  Paperclip,
  File
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const CATEGORIES = [
  { id: 'shopping', label: 'Courses', icon: ShoppingCart, color: 'bg-green-100 text-green-700' },
  { id: 'incident', label: 'Incidents', icon: AlertTriangle, color: 'bg-red-100 text-red-700' },
  { id: 'investment', label: 'Investissements', icon: Lightbulb, color: 'bg-amber-100 text-amber-700' },
  { id: 'announcement', label: 'Annonces', icon: Megaphone, color: 'bg-blue-100 text-blue-700' },
  { id: 'operations', label: 'Fonctionnement', icon: Settings2, color: 'bg-purple-100 text-purple-700' },
  { id: 'vitals', label: 'Éléments vitaux', icon: Zap, color: 'bg-orange-100 text-orange-700' },
  { id: 'intern', label: 'Stagiaires', icon: GraduationCap, color: 'bg-cyan-100 text-cyan-700' },
  { id: 'purchases', label: 'Historique achats', icon: Receipt, color: 'bg-emerald-100 text-emerald-700' },
];

const VISIBILITY_OPTIONS = [
  { id: 'agency_only', label: 'Agence uniquement', icon: EyeOff },
  { id: 'agency_pm', label: 'Agence + Chefs de projet', icon: Eye },
  { id: 'agency_team', label: 'Agence + Membres équipe', icon: Eye },
  { id: 'all', label: 'Tous (+ Clients)', icon: Eye },
];

const INCIDENT_STATUS = [
  { id: 'open', label: 'Ouvert', color: 'bg-red-100 text-red-700' },
  { id: 'in_progress', label: 'En cours', color: 'bg-amber-100 text-amber-700' },
  { id: 'resolved', label: 'Résolu', color: 'bg-green-100 text-green-700' },
];

const INCIDENT_TYPES = [
  { id: 'internet', label: 'Internet / Réseau', icon: WifiOff, color: 'text-blue-500' },
  { id: 'electricity', label: 'Électricité', icon: Zap, color: 'text-yellow-500' },
  { id: 'water', label: 'Eau / Plomberie', icon: Droplets, color: 'text-cyan-500' },
  { id: 'heating', label: 'Chauffage / Clim', icon: Flame, color: 'text-orange-500' },
  { id: 'security', label: 'Sécurité / Alarme', icon: Shield, color: 'text-red-500' },
  { id: 'equipment', label: 'Équipement / Matériel', icon: Wrench, color: 'text-slate-500' },
  { id: 'building', label: 'Bâtiment / Local', icon: Home, color: 'text-purple-500' },
  { id: 'other', label: 'Autre', icon: AlertTriangle, color: 'text-gray-500' },
];

const PRIORITY_OPTIONS = [
  { id: 'low', label: 'Basse', color: 'text-slate-500' },
  { id: 'normal', label: 'Normale', color: 'text-blue-500' },
  { id: 'high', label: 'Haute', color: 'text-orange-500' },
  { id: 'urgent', label: 'Urgente', color: 'text-red-500' },
];

const AgencyManifestPage = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [interns, setInterns] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [purchaseStats, setPurchaseStats] = useState(null);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'shopping');
  
  // Modal states
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [showInternModal, setShowInternModal] = useState(false);
  const [showTimelineModal, setShowTimelineModal] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editingIntern, setEditingIntern] = useState(null);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [selectedShoppingList, setSelectedShoppingList] = useState(null);
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  
  // Intern account/project modals
  const [showCreateAccountModal, setShowCreateAccountModal] = useState(false);
  const [showAssignProjectModal, setShowAssignProjectModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedInternForAction, setSelectedInternForAction] = useState(null);
  const [accountForm, setAccountForm] = useState({ email: '', password: '' });
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [assigningProject, setAssigningProject] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    monday: { start: '09:00', end: '17:00' },
    tuesday: { start: '09:00', end: '17:00' },
    wednesday: { start: '09:00', end: '17:00' },
    thursday: { start: '09:00', end: '17:00' },
    friday: { start: '09:00', end: '17:00' }
  });
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [absenceRequests, setAbsenceRequests] = useState([]);
  
  const fileInputRef = useRef(null);
  
  // Form states
  const [formData, setFormData] = useState({
    title: '',
    category: 'shopping',
    description: '',
    visibility: 'agency_pm',
    incident_status: 'open',
    incident_type: 'other',
    priority: 'normal',
    show_in_banner: false,
    banner_message: '',
    expected_resolution: '',
    location: '',
    items: [],
    budget_estimate: '',
    images: [],
    documents: []
  });
  
  const [internForm, setInternForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    school: '',
    start_date: '',
    end_date: '',
    department: '',
    notes: ''
  });
  
  const [timelineForm, setTimelineForm] = useState({
    contact_type: 'phone',
    description: '',
    contact_person: '',
    result: ''
  });
  
  const [purchaseForm, setPurchaseForm] = useState({
    store_name: '',
    purchase_date: new Date().toISOString().split('T')[0],
    items: [],
    total_amount: 0,
    payment_method: 'card',
    receipt_image: '',
    notes: ''
  });
  
  // Shopping list item
  const [newItem, setNewItem] = useState({ name: '', quantity: 1, urgent: false });
  const [newPurchaseItem, setNewPurchaseItem] = useState({ name: '', quantity: 1, unit_price: 0 });
  
  // New states for enhanced features
  const [showInternDocModal, setShowInternDocModal] = useState(false);
  const [selectedInternForDocs, setSelectedInternForDocs] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [graphData, setGraphData] = useState([]);
  const [graphPeriod, setGraphPeriod] = useState('year');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchingExpenses, setSearchingExpenses] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const internDocInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, [activeTab]);
  
  useEffect(() => {
    setSearchParams({ tab: activeTab });
  }, [activeTab]);
  
  // Fetch graph data when on purchases tab
  useEffect(() => {
    if (activeTab === 'purchases') {
      fetchGraphData();
    }
  }, [activeTab, graphPeriod]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'intern') {
        const [internsRes, requestsRes] = await Promise.all([
          api.get('/manifest/interns'),
          api.get('/manifest/absence-requests?status=pending')
        ]);
        setInterns(internsRes.data || []);
        setAbsenceRequests(requestsRes.data || []);
      } else if (activeTab === 'purchases') {
        const [purchasesRes, statsRes] = await Promise.all([
          api.get('/manifest/purchases'),
          api.get('/manifest/purchases/stats?period=month')
        ]);
        setPurchases(purchasesRes.data?.purchases || []);
        setPurchaseStats(statsRes.data);
      } else {
        const res = await api.get(`/manifest/entries?category=${activeTab}`);
        setEntries(res.data || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch graph data for expense tracking
  const fetchGraphData = async (keyword = null) => {
    try {
      const params = new URLSearchParams({ period: graphPeriod });
      if (keyword) params.append('keyword', keyword);
      
      const res = await api.get(`/manifest/purchases/graph-data?${params}`);
      setGraphData(res.data?.data || []);
      
      if (keyword && res.data) {
        setSearchResults({
          keyword: res.data.keyword,
          total: res.data.keyword_total,
          matches: res.data.keyword_matches
        });
      }
    } catch (error) {
      console.error('Error fetching graph data:', error);
    }
  };
  
  // Search expenses by keyword
  const handleSearchExpenses = async () => {
    if (!searchKeyword.trim()) {
      setSearchResults(null);
      fetchGraphData();
      return;
    }
    
    setSearchingExpenses(true);
    try {
      const res = await api.get(`/manifest/purchases/search?query=${encodeURIComponent(searchKeyword)}`);
      setSearchResults(res.data);
      // Also update graph with keyword filter
      fetchGraphData(searchKeyword);
    } catch (error) {
      toast.error('Erreur lors de la recherche');
    } finally {
      setSearchingExpenses(false);
    }
  };
  
  // Upload document for intern
  const handleInternDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedInternForDocs) return;
    
    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('doc_type', 'other');
      formData.append('doc_name', file.name);
      
      await api.post(`/manifest/interns/${selectedInternForDocs.id}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Document ajouté');
      // Refresh intern data
      const res = await api.get('/manifest/interns');
      setInterns(res.data || []);
      
      // Update selected intern
      const updated = res.data.find(i => i.id === selectedInternForDocs.id);
      if (updated) setSelectedInternForDocs(updated);
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploadingDoc(false);
      e.target.value = '';
    }
  };
  
  // Delete intern document
  const handleDeleteInternDoc = async (docId) => {
    if (!selectedInternForDocs || !window.confirm('Supprimer ce document ?')) return;
    
    try {
      await api.delete(`/manifest/interns/${selectedInternForDocs.id}/documents/${docId}`);
      toast.success('Document supprimé');
      
      // Refresh
      const res = await api.get('/manifest/interns');
      setInterns(res.data || []);
      const updated = res.data.find(i => i.id === selectedInternForDocs.id);
      if (updated) setSelectedInternForDocs(updated);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };
  
  // Delete intern
  const handleDeleteIntern = async (internId) => {
    if (!window.confirm('Supprimer ce stagiaire ? Cette action est irréversible.')) return;
    
    try {
      await api.delete(`/manifest/interns/${internId}`);
      toast.success('Stagiaire supprimé');
      fetchData();
      // Close doc modal if it was for this intern
      if (selectedInternForDocs?.id === internId) {
        setShowInternDocModal(false);
        setSelectedInternForDocs(null);
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };
  
  // Create intern account
  const handleCreateAccount = async () => {
    if (!selectedInternForAction || !accountForm.email || !accountForm.password) {
      toast.error('Email et mot de passe requis');
      return;
    }
    if (accountForm.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setCreatingAccount(true);
    try {
      await api.post(`/manifest/interns/${selectedInternForAction.id}/create-account`, accountForm);
      toast.success('Compte créé ! Le stagiaire peut maintenant se connecter');
      setShowCreateAccountModal(false);
      setAccountForm({ email: '', password: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreatingAccount(false);
    }
  };
  
  // Fetch projects for assignment
  const fetchProjects = async () => {
    try {
      const res = await api.get('/projects');
      setProjects(res.data?.projects || res.data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };
  
  // Assign project to intern
  const handleAssignProject = async () => {
    if (!selectedInternForAction || !selectedProjectId) {
      toast.error('Sélectionnez un projet');
      return;
    }
    
    setAssigningProject(true);
    try {
      await api.post(`/manifest/interns/${selectedInternForAction.id}/assign-project`, { project_id: selectedProjectId });
      toast.success('Projet assigné au stagiaire');
      setShowAssignProjectModal(false);
      setSelectedProjectId('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'assignation');
    } finally {
      setAssigningProject(false);
    }
  };
  
  // Save intern schedule
  const handleSaveSchedule = async () => {
    if (!selectedInternForAction) return;
    
    setSavingSchedule(true);
    try {
      await api.put(`/manifest/interns/${selectedInternForAction.id}/schedule`, scheduleForm);
      toast.success('Horaires mis à jour');
      setShowScheduleModal(false);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSavingSchedule(false);
    }
  };
  
  // Fetch absence requests
  const fetchAbsenceRequests = async () => {
    try {
      const res = await api.get('/manifest/absence-requests?status=pending');
      setAbsenceRequests(res.data || []);
    } catch (error) {
      console.error('Error fetching absence requests:', error);
    }
  };
  
  // Handle absence request decision
  const handleAbsenceRequestDecision = async (requestId, status) => {
    try {
      await api.put(`/manifest/absence-requests/${requestId}`, { status });
      toast.success(`Demande ${status === 'approved' ? 'approuvée' : 'refusée'}`);
      fetchAbsenceRequests();
    } catch (error) {
      toast.error('Erreur lors du traitement');
    }
  };
  
  // Open create account modal
  const openCreateAccountModal = (intern) => {
    setSelectedInternForAction(intern);
    setAccountForm({ email: intern.email || '', password: '' });
    setShowCreateAccountModal(true);
  };
  
  // Open assign project modal
  const openAssignProjectModal = (intern) => {
    if (!intern.user_id) {
      toast.error('Le stagiaire doit d\'abord avoir un compte');
      return;
    }
    setSelectedInternForAction(intern);
    fetchProjects();
    setShowAssignProjectModal(true);
  };
  
  // Open schedule modal
  const openScheduleModal = (intern) => {
    setSelectedInternForAction(intern);
    setScheduleForm(intern.schedule || {
      monday: { start: '09:00', end: '17:00' },
      tuesday: { start: '09:00', end: '17:00' },
      wednesday: { start: '09:00', end: '17:00' },
      thursday: { start: '09:00', end: '17:00' },
      friday: { start: '09:00', end: '17:00' }
    });
    setShowScheduleModal(true);
  };
  
  // Generate PDF report for incident
  const generateIncidentPdf = async (incident) => {
    setGeneratingPdf(true);
    try {
      // Get full report data from API
      const res = await api.get(`/manifest/incidents/${incident.id}/report-data`);
      const { incident: incidentData, agency, generated_at, generated_by } = res.data;
      
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Header with agency info
      doc.setFontSize(20);
      doc.setTextColor(30, 58, 138); // Blue
      doc.text(agency.name || 'Agence MSC', 14, 25);
      
      doc.setFontSize(10);
      doc.setTextColor(100);
      if (agency.address) doc.text(agency.address, 14, 32);
      if (agency.postal_code || agency.city) {
        doc.text(`${agency.postal_code || ''} ${agency.city || ''}`.trim(), 14, 37);
      }
      if (agency.phone) doc.text(`Tél: ${agency.phone}`, 14, 42);
      if (agency.email) doc.text(agency.email, 14, 47);
      if (agency.siret) doc.text(`SIRET: ${agency.siret}`, 14, 52);
      
      // Title
      doc.setFontSize(16);
      doc.setTextColor(0);
      doc.text('RAPPORT D\'INCIDENT', pageWidth / 2, 70, { align: 'center' });
      
      // Horizontal line
      doc.setDrawColor(200);
      doc.line(14, 75, pageWidth - 14, 75);
      
      // Incident details
      let yPos = 85;
      
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text('Informations générales', 14, yPos);
      
      yPos += 8;
      doc.setFont(undefined, 'normal');
      doc.setFontSize(10);
      
      const typeLabels = {
        internet: 'Internet / Réseau',
        electricity: 'Électricité',
        water: 'Eau / Plomberie',
        heating: 'Chauffage / Clim',
        security: 'Sécurité / Alarme',
        equipment: 'Équipement / Matériel',
        building: 'Bâtiment / Local',
        other: 'Autre'
      };
      
      const statusLabels = {
        open: 'Ouvert',
        in_progress: 'En cours',
        resolved: 'Résolu'
      };
      
      doc.text(`Titre: ${incidentData.title || 'N/A'}`, 14, yPos);
      yPos += 6;
      doc.text(`Type: ${typeLabels[incidentData.incident_type] || incidentData.incident_type || 'N/A'}`, 14, yPos);
      yPos += 6;
      doc.text(`Statut: ${statusLabels[incidentData.incident_status] || incidentData.incident_status || 'N/A'}`, 14, yPos);
      yPos += 6;
      doc.text(`Priorité: ${incidentData.priority || 'Normale'}`, 14, yPos);
      yPos += 6;
      doc.text(`Date de création: ${new Date(incidentData.created_at).toLocaleString('fr-FR')}`, 14, yPos);
      yPos += 6;
      doc.text(`Créé par: ${incidentData.created_by_name || 'N/A'}`, 14, yPos);
      
      if (incidentData.location) {
        yPos += 6;
        doc.text(`Localisation: ${incidentData.location}`, 14, yPos);
      }
      
      // Description
      yPos += 12;
      doc.setFont(undefined, 'bold');
      doc.text('Description', 14, yPos);
      doc.setFont(undefined, 'normal');
      yPos += 6;
      
      if (incidentData.description) {
        const descLines = doc.splitTextToSize(incidentData.description, pageWidth - 28);
        doc.text(descLines, 14, yPos);
        yPos += descLines.length * 5;
      } else {
        doc.text('Aucune description', 14, yPos);
        yPos += 5;
      }
      
      // Timeline
      if (incidentData.timeline && incidentData.timeline.length > 0) {
        yPos += 10;
        doc.setFont(undefined, 'bold');
        doc.text('Historique des échanges', 14, yPos);
        yPos += 8;
        
        const tableData = incidentData.timeline.map(t => [
          new Date(t.created_at).toLocaleString('fr-FR'),
          t.contact_type === 'phone' ? 'Téléphone' : t.contact_type === 'email' ? 'Email' : t.contact_type === 'on_site' ? 'Sur place' : 'Autre',
          t.contact_person || '-',
          t.description?.substring(0, 50) + (t.description?.length > 50 ? '...' : '') || '-',
          t.result?.substring(0, 30) + (t.result?.length > 30 ? '...' : '') || '-'
        ]);
        
        doc.autoTable({
          startY: yPos,
          head: [['Date', 'Type', 'Contact', 'Description', 'Résultat']],
          body: tableData,
          theme: 'striped',
          headStyles: { fillColor: [30, 58, 138] },
          styles: { fontSize: 8, cellPadding: 2 },
          columnStyles: {
            0: { cellWidth: 35 },
            1: { cellWidth: 25 },
            2: { cellWidth: 30 },
            3: { cellWidth: 50 },
            4: { cellWidth: 40 }
          }
        });
        
        yPos = doc.lastAutoTable.finalY + 10;
      }
      
      // Resolution info
      if (incidentData.incident_status === 'resolved' && incidentData.resolution_date) {
        doc.setFont(undefined, 'bold');
        doc.text('Résolution', 14, yPos);
        doc.setFont(undefined, 'normal');
        yPos += 6;
        doc.text(`Date de résolution: ${new Date(incidentData.resolution_date).toLocaleString('fr-FR')}`, 14, yPos);
        if (incidentData.resolution_notes) {
          yPos += 6;
          const resLines = doc.splitTextToSize(`Notes: ${incidentData.resolution_notes}`, pageWidth - 28);
          doc.text(resLines, 14, yPos);
        }
      }
      
      // Footer
      const footerY = doc.internal.pageSize.getHeight() - 20;
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(`Rapport généré le ${new Date(generated_at).toLocaleString('fr-FR')} par ${generated_by}`, 14, footerY);
      doc.text(`Document à usage interne - ${agency.name}`, pageWidth / 2, footerY + 5, { align: 'center' });
      
      // Save
      const filename = `incident_${incidentData.id}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(filename);
      
      toast.success('PDF généré avec succès');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Erreur lors de la génération du PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const openCreateModal = () => {
    setEditingEntry(null);
    setFormData({
      title: '',
      category: activeTab,
      description: '',
      visibility: 'agency_pm',
      incident_status: 'open',
      incident_type: 'other',
      priority: 'normal',
      show_in_banner: false,
      banner_message: '',
      expected_resolution: '',
      location: '',
      items: [],
      budget_estimate: '',
      images: [],
      documents: []
    });
    setShowEntryModal(true);
  };
  
  const openEditModal = (entry) => {
    setEditingEntry(entry);
    setFormData({
      title: entry.title || '',
      category: entry.category || activeTab,
      description: entry.description || '',
      visibility: entry.visibility || 'agency_pm',
      incident_status: entry.incident_status || 'open',
      incident_type: entry.incident_type || 'other',
      priority: entry.priority || 'normal',
      show_in_banner: entry.show_in_banner || false,
      banner_message: entry.banner_message || '',
      expected_resolution: entry.expected_resolution || '',
      location: entry.location || '',
      items: entry.items || [],
      budget_estimate: entry.budget_estimate || '',
      images: entry.images || [],
      documents: entry.documents || []
    });
    setShowEntryModal(true);
  };

  const handleSaveEntry = async () => {
    if (!formData.title.trim()) {
      toast.error('Le titre est requis');
      return;
    }
    
    setSaving(true);
    try {
      const endpoint = editingEntry 
        ? `/manifest/entries/${editingEntry.id}` 
        : '/manifest/entries/v2';
      
      if (editingEntry) {
        await api.put(endpoint, formData);
        toast.success('Entrée mise à jour');
      } else {
        await api.post(endpoint, formData);
        toast.success('Entrée créée');
      }
      setShowEntryModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Supprimer cette entrée ?')) return;
    
    try {
      await api.delete(`/manifest/entries/${entryId}`);
      toast.success('Entrée supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const addShoppingItem = () => {
    if (!newItem.name.trim()) return;
    setFormData({
      ...formData,
      items: [...formData.items, { ...newItem, id: Date.now(), purchased: false }]
    });
    setNewItem({ name: '', quantity: 1, urgent: false });
  };

  const toggleItemPurchased = async (entry, itemId) => {
    const updatedItems = entry.items.map(item =>
      item.id === itemId ? { ...item, purchased: !item.purchased } : item
    );
    
    try {
      await api.put(`/manifest/entries/${entry.id}`, { items: updatedItems });
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Purchase & Receipt functions
  const openPurchaseModal = (shoppingList = null) => {
    setSelectedShoppingList(shoppingList);
    setPurchaseForm({
      store_name: '',
      purchase_date: new Date().toISOString().split('T')[0],
      items: shoppingList?.items?.filter(i => !i.purchased).map(i => ({
        name: i.name,
        quantity: i.quantity,
        unit_price: 0,
        total_price: 0
      })) || [],
      total_amount: 0,
      payment_method: 'card',
      receipt_image: '',
      notes: ''
    });
    setShowPurchaseModal(true);
  };

  const handleReceiptUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check file type
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Format non supporté. Utilisez JPG, PNG ou WEBP.');
      return;
    }
    
    setAnalyzing(true);
    
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1];
        
        // Store the image
        setPurchaseForm(prev => ({ ...prev, receipt_image: reader.result }));
        
        // Analyze with AI
        try {
          const res = await api.post('/manifest/analyze-receipt', {
            image_base64: base64,
            mime_type: file.type
          });
          
          if (res.data.success) {
            const data = res.data.data;
            
            // Update form with extracted data
            setPurchaseForm(prev => ({
              ...prev,
              store_name: data.store_name || prev.store_name,
              purchase_date: data.purchase_date || prev.purchase_date,
              items: data.items?.map(item => ({
                name: item.name,
                quantity: item.quantity || 1,
                unit_price: item.unit_price || 0,
                total_price: item.total_price || 0
              })) || prev.items,
              total_amount: data.total || prev.total_amount,
              payment_method: data.payment_method?.toLowerCase().includes('cb') ? 'card' : 
                             data.payment_method?.toLowerCase().includes('esp') ? 'cash' : 'card'
            }));
            
            toast.success('Ticket analysé avec succès !');
          } else {
            toast.error('Impossible d\'extraire les données du ticket');
          }
        } catch (err) {
          console.error('Analysis error:', err);
          toast.error('Erreur lors de l\'analyse du ticket');
        }
        
        setAnalyzing(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erreur lors du chargement');
      setAnalyzing(false);
    }
  };

  const addPurchaseItem = () => {
    if (!newPurchaseItem.name.trim()) return;
    const total = newPurchaseItem.quantity * newPurchaseItem.unit_price;
    setPurchaseForm(prev => ({
      ...prev,
      items: [...prev.items, { ...newPurchaseItem, total_price: total }],
      total_amount: prev.total_amount + total
    }));
    setNewPurchaseItem({ name: '', quantity: 1, unit_price: 0 });
  };

  const handleSavePurchase = async () => {
    if (!purchaseForm.store_name.trim()) {
      toast.error('Le nom du magasin est requis');
      return;
    }
    
    setSaving(true);
    try {
      await api.post('/manifest/purchases', {
        ...purchaseForm,
        shopping_entry_id: selectedShoppingList?.id
      });
      toast.success('Achat enregistré');
      setShowPurchaseModal(false);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  // Intern functions
  const openCreateInternModal = () => {
    setEditingIntern(null);
    setInternForm({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      school: '',
      start_date: '',
      end_date: '',
      department: '',
      notes: ''
    });
    setShowInternModal(true);
  };
  
  const openEditInternModal = (intern) => {
    setEditingIntern(intern);
    setInternForm({
      first_name: intern.first_name || '',
      last_name: intern.last_name || '',
      email: intern.email || '',
      phone: intern.phone || '',
      school: intern.school || '',
      start_date: intern.start_date?.split('T')[0] || '',
      end_date: intern.end_date?.split('T')[0] || '',
      department: intern.department || '',
      notes: intern.notes || ''
    });
    setShowInternModal(true);
  };

  const handleSaveIntern = async () => {
    if (!internForm.first_name.trim() || !internForm.last_name.trim()) {
      toast.error('Nom et prénom requis');
      return;
    }
    
    setSaving(true);
    try {
      if (editingIntern) {
        await api.put(`/manifest/interns/${editingIntern.id}`, internForm);
        toast.success('Stagiaire mis à jour');
      } else {
        await api.post('/manifest/interns', internForm);
        toast.success('Stagiaire ajouté');
      }
      setShowInternModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const toggleInternPresence = async (intern) => {
    try {
      await api.put(`/manifest/interns/${intern.id}/presence`, { is_present: !intern.is_present });
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Timeline functions
  const openTimelineModal = (incident) => {
    setSelectedIncident(incident);
    setTimelineForm({
      contact_type: 'phone',
      description: '',
      contact_person: '',
      result: ''
    });
    setShowTimelineModal(true);
  };

  const handleAddTimeline = async () => {
    if (!timelineForm.description.trim()) {
      toast.error('Description requise');
      return;
    }
    
    setSaving(true);
    try {
      await api.post(`/manifest/entries/${selectedIncident.id}/timeline`, timelineForm);
      toast.success('Échange ajouté');
      setShowTimelineModal(false);
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleResolveIncident = async (incident) => {
    const notes = prompt('Notes de résolution :');
    if (notes === null) return;
    
    try {
      await api.put(`/manifest/incidents/${incident.id}/resolve`, { resolution_notes: notes || 'Résolu' });
      toast.success('Incident marqué comme résolu');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const getCategoryConfig = (categoryId) => {
    return CATEGORIES.find(c => c.id === categoryId) || CATEGORIES[0];
  };

  const getStatusConfig = (statusId) => {
    return INCIDENT_STATUS.find(s => s.id === statusId) || INCIDENT_STATUS[0];
  };

  const getIncidentTypeConfig = (typeId) => {
    return INCIDENT_TYPES.find(t => t.id === typeId) || INCIDENT_TYPES[INCIDENT_TYPES.length - 1];
  };

  const renderEntryCard = (entry) => {
    const category = getCategoryConfig(entry.category);
    const CategoryIcon = category.icon;
    
    return (
      <Card key={entry.id} className="card hover:shadow-md transition-shadow">
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className={cn("p-2 rounded-lg shrink-0", category.color)}>
                <CategoryIcon className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-slate-800">{entry.title}</h3>
                  {entry.category === 'incident' && (
                    <>
                      <Badge className={cn("text-xs", getStatusConfig(entry.incident_status).color)}>
                        {getStatusConfig(entry.incident_status).label}
                      </Badge>
                      {entry.incident_type && (
                        <Badge variant="outline" className="text-xs">
                          {getIncidentTypeConfig(entry.incident_type).label}
                        </Badge>
                      )}
                    </>
                  )}
                  {entry.show_in_banner && (
                    <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                      Bandeau actif
                    </Badge>
                  )}
                </div>
                {entry.description && (
                  <p className="text-sm text-slate-600 mt-1 line-clamp-2">{entry.description}</p>
                )}
                {entry.location && (
                  <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {entry.location}
                  </p>
                )}
                <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                  <span className="flex items-center gap-1">
                    <User className="w-3 h-3" />
                    {entry.created_by_name}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {new Date(entry.created_at).toLocaleDateString('fr-FR')}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-1 shrink-0">
              {entry.category === 'shopping' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openPurchaseModal(entry)}
                  className="h-8 px-2 text-green-600"
                  title="Enregistrer un achat"
                >
                  <Receipt className="w-4 h-4" />
                </Button>
              )}
              {entry.category === 'incident' && (
                <>
                  {entry.incident_status !== 'resolved' && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openTimelineModal(entry)}
                        className="h-8 w-8 p-0"
                        title="Ajouter un échange"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleResolveIncident(entry)}
                        className="h-8 w-8 p-0 text-green-600"
                        title="Marquer comme résolu"
                      >
                        <CheckCircle2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => generateIncidentPdf(entry)}
                    className="h-8 w-8 p-0 text-blue-600"
                    title="Générer rapport PDF"
                    disabled={generatingPdf}
                  >
                    {generatingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  </Button>
                </>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => openEditModal(entry)}
                className="h-8 w-8 p-0"
              >
                <Edit className="w-4 h-4" />
              </Button>
              {user?.role === 'agency' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteEntry(entry.id)}
                  className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
          
          {/* Shopping list items */}
          {entry.category === 'shopping' && entry.items?.length > 0 && (
            <div className="mt-3 space-y-1 border-t pt-3">
              {entry.items.map((item, idx) => (
                <div key={item.id || idx} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={item.purchased}
                    onCheckedChange={() => toggleItemPurchased(entry, item.id)}
                  />
                  <span className={cn(item.purchased && "line-through text-slate-400")}>
                    {item.name} {item.quantity > 1 && `(x${item.quantity})`}
                  </span>
                  {item.urgent && (
                    <Badge variant="destructive" className="text-xs">Urgent</Badge>
                  )}
                  {item.purchased && item.purchased_at && (
                    <span className="text-xs text-green-600">
                      ✓ {new Date(item.purchased_at).toLocaleDateString('fr-FR')}
                    </span>
                  )}
                </div>
              ))}
              {entry.budget_estimate && (
                <div className="mt-2 text-xs text-slate-500">
                  Budget estimé: {entry.budget_estimate}€
                  {entry.total_spent > 0 && ` | Dépensé: ${entry.total_spent}€`}
                </div>
              )}
            </div>
          )}
          
          {/* Incident timeline */}
          {entry.category === 'incident' && entry.timeline?.length > 0 && (
            <div className="mt-3 border-t pt-3">
              <p className="text-xs font-medium text-slate-500 mb-2 flex items-center gap-1">
                <History className="w-3 h-3" />
                Suivi des échanges ({entry.timeline.length})
              </p>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {entry.timeline.map((t, idx) => (
                  <div key={t.id || idx} className="flex items-start gap-2 text-xs bg-slate-50 p-2 rounded">
                    {t.contact_type === 'phone' && <Phone className="w-3 h-3 text-blue-500 mt-0.5" />}
                    {t.contact_type === 'email' && <Mail className="w-3 h-3 text-green-500 mt-0.5" />}
                    {t.contact_type === 'on_site' && <Building2 className="w-3 h-3 text-purple-500 mt-0.5" />}
                    {t.contact_type === 'resolution' && <CheckCircle2 className="w-3 h-3 text-green-600 mt-0.5" />}
                    <div className="flex-1">
                      <p className="text-slate-700">{t.description}</p>
                      {t.contact_person && <p className="text-slate-500">Contact: {t.contact_person}</p>}
                      {t.result && <p className="text-slate-500">Résultat: {t.result}</p>}
                      <p className="text-slate-400 mt-1">
                        {new Date(t.created_at).toLocaleString('fr-FR')} - {t.created_by_name}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Resolution info */}
          {entry.category === 'incident' && entry.incident_status === 'resolved' && entry.resolution_date && (
            <div className="mt-3 p-2 bg-green-50 rounded-lg text-xs">
              <p className="font-medium text-green-700 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" />
                Résolu le {new Date(entry.resolution_date).toLocaleString('fr-FR')}
              </p>
              {entry.resolution_notes && (
                <p className="text-green-600 mt-1">{entry.resolution_notes}</p>
              )}
            </div>
          )}
          
          {/* Images */}
          {entry.images?.length > 0 && (
            <div className="mt-3 flex gap-2 flex-wrap">
              {entry.images.map((img, idx) => (
                <a key={idx} href={img} target="_blank" rel="noopener noreferrer">
                  <img src={img} alt="" className="w-16 h-16 object-cover rounded border" />
                </a>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderPurchasesTab = () => (
    <div className="space-y-6">
      {/* Search and filters */}
      <Card className="card">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Keyword search */}
            <div className="flex-1">
              <Label className="text-xs text-slate-500 mb-1 block">Recherche par mot-clé</Label>
              <div className="flex gap-2">
                <Input
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  placeholder="Ex: café, fournitures, eau..."
                  className="flex-1"
                  onKeyDown={(e) => e.key === 'Enter' && handleSearchExpenses()}
                />
                <Button onClick={handleSearchExpenses} disabled={searchingExpenses}>
                  {searchingExpenses ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
                {searchResults && (
                  <Button variant="outline" onClick={() => { setSearchKeyword(''); setSearchResults(null); fetchGraphData(); }}>
                    <XCircle className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
            
            {/* Period filter */}
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Période</Label>
              <Select value={graphPeriod} onValueChange={setGraphPeriod}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="month">30 jours</SelectItem>
                  <SelectItem value="quarter">3 mois</SelectItem>
                  <SelectItem value="year">12 mois</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Search results summary */}
          {searchResults && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-blue-800">
                    Résultats pour "{searchResults.query || searchKeyword}"
                  </p>
                  <p className="text-sm text-blue-600">
                    {searchResults.total_results || searchResults.matches?.length || 0} achats trouvés • 
                    Total: <span className="font-bold">{(searchResults.total_spent || searchResults.total || 0).toFixed(2)}€</span>
                  </p>
                </div>
                <BarChart3 className="w-8 h-8 text-blue-400" />
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Expenses Graph */}
      <Card className="card">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-600" />
            Évolution des dépenses
            {searchKeyword && searchResults && (
              <Badge variant="secondary" className="ml-2">{searchKeyword}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {graphData.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={graphData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" tickFormatter={(val) => `${val}€`} />
                  <Tooltip 
                    formatter={(value) => [`${value.toFixed(2)}€`, 'Dépenses']}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                  />
                  <Bar dataKey="total" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-slate-400">
              <div className="text-center">
                <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Aucune donnée pour cette période</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Stats */}
      {purchaseStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <Receipt className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Total achats</p>
                  <p className="text-xl font-bold">{purchaseStats.total_purchases}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Euro className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Total dépensé</p>
                  <p className="text-xl font-bold">{purchaseStats.total_spent?.toFixed(2)}€</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card col-span-2">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500 mb-2">Par catégorie</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(purchaseStats.by_category || {}).map(([cat, amount]) => (
                  <Badge key={cat} variant="outline" className="text-xs">
                    {cat}: {amount.toFixed(2)}€
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Purchase history */}
      <div className="space-y-4">
        {purchases.map(purchase => (
          <Card key={purchase.id} className="card">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <Store className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold">{purchase.store_name}</h4>
                    <p className="text-sm text-slate-500">
                      {new Date(purchase.purchase_date).toLocaleDateString('fr-FR')}
                      {' • '}
                      {purchase.payment_method === 'card' ? 'Carte' : purchase.payment_method === 'cash' ? 'Espèces' : 'Carte entreprise'}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-emerald-600">{purchase.total_amount?.toFixed(2)}€</p>
                  <p className="text-xs text-slate-500">{purchase.items?.length || 0} articles</p>
                </div>
              </div>
              
              {/* Items */}
              <div className="mt-3 pt-3 border-t">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                  {purchase.items?.slice(0, 6).map((item, idx) => (
                    <div key={idx} className="flex justify-between text-slate-600">
                      <span className="truncate">{item.name}</span>
                      <span className="text-slate-400 ml-2">{item.total_price?.toFixed(2)}€</span>
                    </div>
                  ))}
                  {purchase.items?.length > 6 && (
                    <span className="text-slate-400 text-xs">+{purchase.items.length - 6} autres</span>
                  )}
                </div>
              </div>
              
              {/* Receipt image */}
              {purchase.receipt_image && (
                <div className="mt-3">
                  <a href={purchase.receipt_image} target="_blank" rel="noopener noreferrer">
                    <img src={purchase.receipt_image} alt="Ticket" className="h-20 rounded border" />
                  </a>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
        
        {purchases.length === 0 && (
          <Card className="card">
            <CardContent className="py-12 text-center">
              <Receipt className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">Aucun achat enregistré</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );

  const renderInternsTab = () => (
    <div className="space-y-4">
      {/* Active interns */}
      <div>
        <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
          <UserCheck className="w-5 h-5 text-green-600" />
          Stagiaires actuellement présents
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {interns.filter(i => i.is_active).map(intern => (
            <Card key={intern.id} className="card">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold",
                      intern.is_present ? "bg-green-500" : "bg-slate-400"
                    )}>
                      {intern.first_name?.[0]}{intern.last_name?.[0]}
                    </div>
                    <div>
                      <h4 className="font-semibold">{intern.first_name} {intern.last_name}</h4>
                      <p className="text-xs text-slate-500">{intern.department || 'Non assigné'}</p>
                      <p className="text-xs text-slate-400">{intern.school}</p>
                    </div>
                  </div>
                  <Button
                    variant={intern.is_present ? "default" : "outline"}
                    size="sm"
                    onClick={() => toggleInternPresence(intern)}
                    className={cn(
                      "h-8",
                      intern.is_present ? "bg-green-600 hover:bg-green-700" : ""
                    )}
                  >
                    {intern.is_present ? (
                      <><UserCheck className="w-4 h-4 mr-1" /> Présent</>
                    ) : (
                      <><UserX className="w-4 h-4 mr-1" /> Absent</>
                    )}
                  </Button>
                </div>
                
                {/* Documents section */}
                {intern.documents && intern.documents.length > 0 && (
                  <div className="mt-3 pt-3 border-t">
                    <div className="flex flex-wrap gap-1">
                      {intern.documents.slice(0, 3).map((doc, idx) => (
                        <a 
                          key={doc.id || idx}
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs bg-slate-100 hover:bg-slate-200 px-2 py-1 rounded"
                          title={doc.name}
                        >
                          <File className="w-3 h-3" />
                          <span className="truncate max-w-[80px]">{doc.name || 'Document'}</span>
                        </a>
                      ))}
                      {intern.documents.length > 3 && (
                        <span className="text-xs text-slate-400 px-2 py-1">+{intern.documents.length - 3}</span>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Account & project status */}
                <div className="mt-3 flex flex-wrap gap-1">
                  {intern.has_account ? (
                    <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">
                      <UserCheck className="w-3 h-3 mr-1" /> Compte actif
                    </Badge>
                  ) : (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => openCreateAccountModal(intern)}
                      className="h-6 text-xs gap-1"
                    >
                      <UserPlus className="w-3 h-3" /> Créer compte
                    </Button>
                  )}
                  {intern.assigned_projects?.length > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      <Briefcase className="w-3 h-3 mr-1" /> {intern.assigned_projects.length} projet(s)
                    </Badge>
                  )}
                </div>
                
                <div className="mt-3 pt-3 border-t text-xs text-slate-500 flex items-center justify-between">
                  <span>Du {new Date(intern.start_date).toLocaleDateString('fr-FR')} au {new Date(intern.end_date).toLocaleDateString('fr-FR')}</span>
                  <div className="flex gap-1">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => openScheduleModal(intern)} 
                      className="h-6 w-6 p-0"
                      title="Définir les horaires"
                    >
                      <CalendarClock className="w-3 h-3" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => openAssignProjectModal(intern)} 
                      className="h-6 w-6 p-0"
                      title="Assigner un projet"
                      disabled={!intern.has_account}
                    >
                      <Briefcase className="w-3 h-3" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => { setSelectedInternForDocs(intern); setShowInternDocModal(true); }} 
                      className="h-6 w-6 p-0"
                      title="Gérer les documents"
                    >
                      <Paperclip className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openEditInternModal(intern)} className="h-6 w-6 p-0" title="Modifier">
                      <Edit className="w-3 h-3" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleDeleteIntern(intern.id)} 
                      className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                      title="Supprimer"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        {interns.filter(i => i.is_active).length === 0 && (
          <p className="text-slate-500 text-sm">Aucun stagiaire actif actuellement</p>
        )}
      </div>
      
      {/* Pending absence requests */}
      {absenceRequests.length > 0 && (
        <Card className="card mt-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-orange-500" />
              Demandes en attente ({absenceRequests.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {absenceRequests.map(req => (
                <div key={req.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium">{req.intern_name}</p>
                    <p className="text-sm text-slate-500">
                      {req.request_type === 'absence' ? 'Absence' : req.request_type === 'early_departure' ? 'Départ anticipé' : 'Arrivée tardive'}
                      {' - '}{new Date(req.date).toLocaleDateString('fr-FR')}
                    </p>
                    <p className="text-sm">{req.reason}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="text-green-600 border-green-600"
                      onClick={() => handleAbsenceRequestDecision(req.id, 'approved')}
                    >
                      <CheckCircle2 className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="text-red-600 border-red-600"
                      onClick={() => handleAbsenceRequestDecision(req.id, 'rejected')}
                    >
                      <XCircle className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Past interns */}
      {interns.filter(i => !i.is_active).length > 0 && (
        <div className="mt-6">
          <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-slate-400" />
            Anciens stagiaires
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {interns.filter(i => !i.is_active).map(intern => (
              <Card key={intern.id} className="card opacity-60">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-slate-300 flex items-center justify-center text-white font-semibold">
                      {intern.first_name?.[0]}{intern.last_name?.[0]}
                    </div>
                    <div>
                      <h4 className="font-semibold">{intern.first_name} {intern.last_name}</h4>
                      <p className="text-xs text-slate-500">{intern.department || 'Non assigné'}</p>
                      <p className="text-xs text-slate-400">
                        {new Date(intern.start_date).toLocaleDateString('fr-FR')} - {new Date(intern.end_date).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <MainLayout>
      <div className="p-4 md:p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold" style={{ color: theme.colors?.primary }}>
              Manifeste Interne
            </h1>
            <p className="text-slate-500 mt-1">Vie en agence, incidents, approvisionnement et stagiaires</p>
          </div>
          <div className="flex gap-2">
            {activeTab === 'shopping' && (
              <Button
                variant="outline"
                onClick={() => openPurchaseModal()}
                className="gap-2"
              >
                <Receipt className="w-4 h-4" />
                Enregistrer un achat
              </Button>
            )}
            <Button
              onClick={activeTab === 'intern' ? openCreateInternModal : activeTab === 'purchases' ? () => openPurchaseModal() : openCreateModal}
              className="gap-2"
              style={{ backgroundColor: theme.colors?.accent }}
            >
              <Plus className="w-4 h-4" />
              {activeTab === 'intern' ? 'Ajouter un stagiaire' : activeTab === 'purchases' ? 'Nouvel achat' : 'Nouvelle entrée'}
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex flex-wrap gap-1 h-auto p-1 bg-slate-100 rounded-xl">
            {CATEGORIES.map(cat => {
              const Icon = cat.icon;
              return (
                <TabsTrigger
                  key={cat.id}
                  value={cat.id}
                  className="flex items-center gap-1.5 px-3 py-2 data-[state=active]:bg-white"
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{cat.label}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {/* Content */}
          <div className="mt-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : activeTab === 'intern' ? (
              renderInternsTab()
            ) : activeTab === 'purchases' ? (
              renderPurchasesTab()
            ) : entries.length === 0 ? (
              <Card className="card">
                <CardContent className="py-12 text-center">
                  <div className={cn("w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4", getCategoryConfig(activeTab).color)}>
                    {React.createElement(getCategoryConfig(activeTab).icon, { className: "w-8 h-8" })}
                  </div>
                  <p className="text-slate-500 mb-4">Aucune entrée dans cette catégorie</p>
                  <Button onClick={openCreateModal} className="gap-2">
                    <Plus className="w-4 h-4" />
                    Créer la première entrée
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {entries.map(entry => renderEntryCard(entry))}
              </div>
            )}
          </div>
        </Tabs>
      </div>

      {/* Entry Modal */}
      <Dialog open={showEntryModal} onOpenChange={setShowEntryModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingEntry ? 'Modifier l\'entrée' : 'Nouvelle entrée'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Titre *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Titre de l'entrée"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Description détaillée..."
                rows={3}
              />
            </div>
            
            <div>
              <Label>Visibilité</Label>
              <Select value={formData.visibility} onValueChange={(v) => setFormData({ ...formData, visibility: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VISIBILITY_OPTIONS.map(opt => (
                    <SelectItem key={opt.id} value={opt.id}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Incident specific fields */}
            {formData.category === 'incident' && (
              <>
                <div>
                  <Label>Type d'incident</Label>
                  <Select value={formData.incident_type} onValueChange={(v) => setFormData({ ...formData, incident_type: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {INCIDENT_TYPES.map(t => {
                        const Icon = t.icon;
                        return (
                          <SelectItem key={t.id} value={t.id}>
                            <span className="flex items-center gap-2">
                              <Icon className={cn("w-4 h-4", t.color)} />
                              {t.label}
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Statut</Label>
                    <Select value={formData.incident_status} onValueChange={(v) => setFormData({ ...formData, incident_status: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {INCIDENT_STATUS.map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Priorité</Label>
                    <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PRIORITY_OPTIONS.map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div>
                  <Label>Date de résolution prévue</Label>
                  <Input
                    type="date"
                    value={formData.expected_resolution}
                    onChange={(e) => setFormData({ ...formData, expected_resolution: e.target.value })}
                  />
                </div>
                
                <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
                  <Switch
                    checked={formData.show_in_banner}
                    onCheckedChange={(v) => setFormData({ ...formData, show_in_banner: v })}
                  />
                  <div>
                    <Label className="text-amber-800">Afficher dans le bandeau d'alerte</Label>
                    <p className="text-xs text-amber-600">L'incident apparaîtra en haut de l'écran</p>
                  </div>
                </div>
                
                {formData.show_in_banner && (
                  <div>
                    <Label>Message du bandeau</Label>
                    <Input
                      value={formData.banner_message}
                      onChange={(e) => setFormData({ ...formData, banner_message: e.target.value })}
                      placeholder="Ex: ⚠️ ATTENTION : coupure électricité prévue le 18/02 à 10h"
                    />
                  </div>
                )}
              </>
            )}
            
            {/* Shopping specific fields */}
            {formData.category === 'shopping' && (
              <>
                <div>
                  <Label>Budget estimé (€)</Label>
                  <Input
                    type="number"
                    value={formData.budget_estimate}
                    onChange={(e) => setFormData({ ...formData, budget_estimate: e.target.value })}
                    placeholder="50"
                  />
                </div>
                <div>
                  <Label>Articles à acheter</Label>
                  <div className="space-y-2 mt-2">
                    {formData.items.map((item, idx) => (
                      <div key={item.id || idx} className="flex items-center gap-2 text-sm bg-slate-50 p-2 rounded">
                        <span className="flex-1">{item.name} (x{item.quantity})</span>
                        {item.urgent && <Badge variant="destructive" className="text-xs">Urgent</Badge>}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setFormData({
                            ...formData,
                            items: formData.items.filter((_, i) => i !== idx)
                          })}
                          className="h-6 w-6 p-0 text-red-500"
                        >
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <Input
                        value={newItem.name}
                        onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                        placeholder="Nom de l'article"
                        className="flex-1"
                      />
                      <Input
                        type="number"
                        value={newItem.quantity}
                        onChange={(e) => setNewItem({ ...newItem, quantity: parseInt(e.target.value) || 1 })}
                        className="w-16"
                        min={1}
                      />
                      <Button variant="outline" size="sm" onClick={() => setNewItem({ ...newItem, urgent: !newItem.urgent })}>
                        {newItem.urgent ? '🔴' : '⚪'}
                      </Button>
                      <Button onClick={addShoppingItem} size="sm">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}
            
            {/* Vitals specific fields */}
            {formData.category === 'vitals' && (
              <div>
                <Label>Emplacement</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Ex: Placard cuisine, sous l'évier"
                />
              </div>
            )}
            
            {/* File upload for incidents and vitals */}
            {(formData.category === 'incident' || formData.category === 'vitals' || formData.category === 'operations') && (
              <div>
                <Label>Photos / Documents</Label>
                <div className="mt-2 space-y-2">
                  {/* Existing images */}
                  {formData.images?.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.images.map((img, idx) => (
                        <div key={idx} className="relative group">
                          <img src={img} alt="" className="w-16 h-16 object-cover rounded border" />
                          <button
                            type="button"
                            onClick={() => setFormData({
                              ...formData,
                              images: formData.images.filter((_, i) => i !== idx)
                            })}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Upload button */}
                  <div className="flex gap-2">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        
                        const formDataUpload = new FormData();
                        formDataUpload.append('file', file);
                        formDataUpload.append('file_type', 'image');
                        
                        try {
                          const res = await api.post('/manifest/upload', formDataUpload, {
                            headers: { 'Content-Type': 'multipart/form-data' }
                          });
                          setFormData(prev => ({
                            ...prev,
                            images: [...(prev.images || []), res.data.url]
                          }));
                          toast.success('Image ajoutée');
                        } catch (err) {
                          toast.error('Erreur lors de l\'upload');
                        }
                        e.target.value = '';
                      }}
                      className="hidden"
                      id="image-upload"
                    />
                    <label
                      htmlFor="image-upload"
                      className="flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer hover:bg-slate-50 text-sm"
                    >
                      <Camera className="w-4 h-4" />
                      Ajouter une photo
                    </label>
                    
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.xls,.xlsx"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        
                        const formDataUpload = new FormData();
                        formDataUpload.append('file', file);
                        formDataUpload.append('file_type', 'document');
                        
                        try {
                          const res = await api.post('/manifest/upload', formDataUpload, {
                            headers: { 'Content-Type': 'multipart/form-data' }
                          });
                          setFormData(prev => ({
                            ...prev,
                            documents: [...(prev.documents || []), res.data.url]
                          }));
                          toast.success('Document ajouté');
                        } catch (err) {
                          toast.error('Erreur lors de l\'upload');
                        }
                        e.target.value = '';
                      }}
                      className="hidden"
                      id="doc-upload"
                    />
                    <label
                      htmlFor="doc-upload"
                      className="flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer hover:bg-slate-50 text-sm"
                    >
                      <FileText className="w-4 h-4" />
                      Ajouter un document
                    </label>
                  </div>
                  
                  {/* Existing documents */}
                  {formData.documents?.length > 0 && (
                    <div className="space-y-1">
                      {formData.documents.map((doc, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                          <a href={doc} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate">
                            {doc.split('/').pop()}
                          </a>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setFormData({
                              ...formData,
                              documents: formData.documents.filter((_, i) => i !== idx)
                            })}
                            className="h-6 w-6 p-0 text-red-500"
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEntryModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveEntry} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingEntry ? 'Enregistrer' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Purchase Modal */}
      <Dialog open={showPurchaseModal} onOpenChange={setShowPurchaseModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5" />
              Enregistrer un achat
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Receipt upload */}
            <div className="border-2 border-dashed rounded-xl p-4 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleReceiptUpload}
                className="hidden"
              />
              
              {purchaseForm.receipt_image ? (
                <div className="space-y-2">
                  <img 
                    src={purchaseForm.receipt_image} 
                    alt="Ticket" 
                    className="max-h-40 mx-auto rounded"
                  />
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Changer l'image
                  </Button>
                </div>
              ) : (
                <div 
                  className="cursor-pointer py-6"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {analyzing ? (
                    <>
                      <Loader2 className="w-10 h-10 mx-auto text-slate-400 animate-spin mb-2" />
                      <p className="text-sm text-slate-600">Analyse du ticket en cours...</p>
                    </>
                  ) : (
                    <>
                      <ScanLine className="w-10 h-10 mx-auto text-slate-400 mb-2" />
                      <p className="text-sm text-slate-600">Cliquez pour scanner un ticket de caisse</p>
                      <p className="text-xs text-slate-400 mt-1">L'IA extraira automatiquement les informations</p>
                    </>
                  )}
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Magasin *</Label>
                <Input
                  value={purchaseForm.store_name}
                  onChange={(e) => setPurchaseForm({ ...purchaseForm, store_name: e.target.value })}
                  placeholder="Carrefour, Leclerc..."
                />
              </div>
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={purchaseForm.purchase_date}
                  onChange={(e) => setPurchaseForm({ ...purchaseForm, purchase_date: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label>Mode de paiement</Label>
              <Select value={purchaseForm.payment_method} onValueChange={(v) => setPurchaseForm({ ...purchaseForm, payment_method: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="card">Carte bancaire</SelectItem>
                  <SelectItem value="cash">Espèces</SelectItem>
                  <SelectItem value="company_card">Carte entreprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Items */}
            <div>
              <Label>Articles achetés</Label>
              <div className="space-y-2 mt-2 max-h-40 overflow-y-auto">
                {purchaseForm.items.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm bg-slate-50 p-2 rounded">
                    <span>{item.name} x{item.quantity}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{item.total_price?.toFixed(2)}€</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const items = purchaseForm.items.filter((_, i) => i !== idx);
                          setPurchaseForm({
                            ...purchaseForm,
                            items,
                            total_amount: items.reduce((sum, i) => sum + (i.total_price || 0), 0)
                          });
                        }}
                        className="h-6 w-6 p-0 text-red-500"
                      >
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <Input
                  value={newPurchaseItem.name}
                  onChange={(e) => setNewPurchaseItem({ ...newPurchaseItem, name: e.target.value })}
                  placeholder="Article"
                  className="flex-1"
                />
                <Input
                  type="number"
                  value={newPurchaseItem.quantity}
                  onChange={(e) => setNewPurchaseItem({ ...newPurchaseItem, quantity: parseInt(e.target.value) || 1 })}
                  className="w-14"
                  min={1}
                />
                <Input
                  type="number"
                  step="0.01"
                  value={newPurchaseItem.unit_price}
                  onChange={(e) => setNewPurchaseItem({ ...newPurchaseItem, unit_price: parseFloat(e.target.value) || 0 })}
                  className="w-20"
                  placeholder="Prix"
                />
                <Button onClick={addPurchaseItem} size="sm">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {/* Total */}
            <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
              <span className="font-medium text-emerald-800">Total</span>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  step="0.01"
                  value={purchaseForm.total_amount}
                  onChange={(e) => setPurchaseForm({ ...purchaseForm, total_amount: parseFloat(e.target.value) || 0 })}
                  className="w-24 text-right font-bold"
                />
                <span className="font-bold text-emerald-700">€</span>
              </div>
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea
                value={purchaseForm.notes}
                onChange={(e) => setPurchaseForm({ ...purchaseForm, notes: e.target.value })}
                placeholder="Notes sur cet achat..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPurchaseModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleSavePurchase} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Enregistrer l'achat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Intern Modal */}
      <Dialog open={showInternModal} onOpenChange={setShowInternModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingIntern ? 'Modifier le stagiaire' : 'Nouveau stagiaire'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Prénom *</Label>
                <Input
                  value={internForm.first_name}
                  onChange={(e) => setInternForm({ ...internForm, first_name: e.target.value })}
                />
              </div>
              <div>
                <Label>Nom *</Label>
                <Input
                  value={internForm.last_name}
                  onChange={(e) => setInternForm({ ...internForm, last_name: e.target.value })}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={internForm.email}
                  onChange={(e) => setInternForm({ ...internForm, email: e.target.value })}
                />
              </div>
              <div>
                <Label>Téléphone</Label>
                <Input
                  value={internForm.phone}
                  onChange={(e) => setInternForm({ ...internForm, phone: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label>École / Établissement</Label>
              <Input
                value={internForm.school}
                onChange={(e) => setInternForm({ ...internForm, school: e.target.value })}
              />
            </div>
            
            <div>
              <Label>Département</Label>
              <Select value={internForm.department} onValueChange={(v) => setInternForm({ ...internForm, department: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Choisir..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="design">Design</SelectItem>
                  <SelectItem value="dev">Développement</SelectItem>
                  <SelectItem value="marketing">Marketing</SelectItem>
                  <SelectItem value="communication">Communication</SelectItem>
                  <SelectItem value="admin">Administration</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date de début</Label>
                <Input
                  type="date"
                  value={internForm.start_date}
                  onChange={(e) => setInternForm({ ...internForm, start_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Date de fin</Label>
                <Input
                  type="date"
                  value={internForm.end_date}
                  onChange={(e) => setInternForm({ ...internForm, end_date: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea
                value={internForm.notes}
                onChange={(e) => setInternForm({ ...internForm, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInternModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveIntern} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingIntern ? 'Enregistrer' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Timeline Modal */}
      <Dialog open={showTimelineModal} onOpenChange={setShowTimelineModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Ajouter un échange</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Type de contact</Label>
              <Select value={timelineForm.contact_type} onValueChange={(v) => setTimelineForm({ ...timelineForm, contact_type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="phone">📞 Téléphone</SelectItem>
                  <SelectItem value="email">📧 Email</SelectItem>
                  <SelectItem value="on_site">🏢 Sur place</SelectItem>
                  <SelectItem value="other">📝 Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Personne contactée</Label>
              <Input
                value={timelineForm.contact_person}
                onChange={(e) => setTimelineForm({ ...timelineForm, contact_person: e.target.value })}
                placeholder="Nom du contact"
              />
            </div>
            
            <div>
              <Label>Description *</Label>
              <Textarea
                value={timelineForm.description}
                onChange={(e) => setTimelineForm({ ...timelineForm, description: e.target.value })}
                placeholder="Détails de l'échange..."
                rows={3}
              />
            </div>
            
            <div>
              <Label>Résultat / Suite à donner</Label>
              <Input
                value={timelineForm.result}
                onChange={(e) => setTimelineForm({ ...timelineForm, result: e.target.value })}
                placeholder="Intervention prévue le..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTimelineModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleAddTimeline} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Intern Documents Modal */}
      <Dialog open={showInternDocModal} onOpenChange={setShowInternDocModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Paperclip className="w-5 h-5" />
              Documents de {selectedInternForDocs?.first_name} {selectedInternForDocs?.last_name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Upload section */}
            <div className="border-2 border-dashed rounded-xl p-4 text-center">
              <input
                ref={internDocInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
                onChange={handleInternDocUpload}
                className="hidden"
              />
              <div 
                className="cursor-pointer py-4"
                onClick={() => internDocInputRef.current?.click()}
              >
                {uploadingDoc ? (
                  <>
                    <Loader2 className="w-10 h-10 mx-auto text-slate-400 animate-spin mb-2" />
                    <p className="text-sm text-slate-600">Upload en cours...</p>
                  </>
                ) : (
                  <>
                    <Upload className="w-10 h-10 mx-auto text-slate-400 mb-2" />
                    <p className="text-sm text-slate-600">Cliquez pour ajouter un document</p>
                    <p className="text-xs text-slate-400 mt-1">PDF, DOC, DOCX, JPG, PNG (max 10 Mo)</p>
                  </>
                )}
              </div>
            </div>
            
            {/* Documents list */}
            <div className="space-y-2">
              <Label className="text-sm text-slate-500">Documents ({selectedInternForDocs?.documents?.length || 0})</Label>
              {selectedInternForDocs?.documents?.length > 0 ? (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {selectedInternForDocs.documents.map((doc, idx) => (
                    <div key={doc.id || idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="p-2 bg-white rounded border">
                          {doc.mime_type?.includes('image') ? (
                            <Image className="w-4 h-4 text-blue-500" />
                          ) : (
                            <FileText className="w-4 h-4 text-red-500" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <a 
                            href={doc.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm font-medium text-slate-700 hover:text-blue-600 truncate block"
                          >
                            {doc.name || doc.original_filename || 'Document'}
                          </a>
                          <p className="text-xs text-slate-400">
                            {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString('fr-FR') : ''}
                            {doc.size ? ` • ${(doc.size / 1024).toFixed(1)} Ko` : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <a 
                          href={doc.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="p-1 hover:bg-slate-200 rounded"
                        >
                          <Download className="w-4 h-4 text-slate-500" />
                        </a>
                        <button 
                          onClick={() => handleDeleteInternDoc(doc.id)}
                          className="p-1 hover:bg-red-100 rounded"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Aucun document pour ce stagiaire</p>
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInternDocModal(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Create Intern Account Modal */}
      <Dialog open={showCreateAccountModal} onOpenChange={setShowCreateAccountModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Créer un compte pour {selectedInternForAction?.first_name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={accountForm.email}
                onChange={(e) => setAccountForm({ ...accountForm, email: e.target.value })}
                placeholder="email@exemple.com"
              />
            </div>
            
            <div>
              <Label>Mot de passe (min 6 caractères)</Label>
              <Input
                type="password"
                value={accountForm.password}
                onChange={(e) => setAccountForm({ ...accountForm, password: e.target.value })}
                placeholder="••••••"
              />
            </div>
            
            <p className="text-sm text-slate-500">
              Le stagiaire pourra se connecter et accéder à son espace personnel.
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateAccountModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreateAccount} disabled={creatingAccount}>
              {creatingAccount && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Créer le compte
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Assign Project Modal */}
      <Dialog open={showAssignProjectModal} onOpenChange={setShowAssignProjectModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="w-5 h-5" />
              Assigner un projet à {selectedInternForAction?.first_name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Sélectionner un projet</Label>
              <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choisir un projet..." />
                </SelectTrigger>
                <SelectContent>
                  {projects.map(project => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <p className="text-sm text-slate-500">
              Le stagiaire aura accès au projet et pourra y contribuer comme un membre d'équipe.
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignProjectModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleAssignProject} disabled={assigningProject || !selectedProjectId}>
              {assigningProject && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Assigner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Schedule Modal */}
      <Dialog open={showScheduleModal} onOpenChange={setShowScheduleModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarClock className="w-5 h-5" />
              Horaires de {selectedInternForAction?.first_name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].map((day) => {
              const dayLabels = {
                monday: 'Lundi',
                tuesday: 'Mardi',
                wednesday: 'Mercredi',
                thursday: 'Jeudi',
                friday: 'Vendredi'
              };
              return (
                <div key={day} className="flex items-center gap-3">
                  <span className="w-24 text-sm font-medium">{dayLabels[day]}</span>
                  <Input
                    type="time"
                    value={scheduleForm[day]?.start || '09:00'}
                    onChange={(e) => setScheduleForm({
                      ...scheduleForm,
                      [day]: { ...scheduleForm[day], start: e.target.value }
                    })}
                    className="w-24"
                  />
                  <span>à</span>
                  <Input
                    type="time"
                    value={scheduleForm[day]?.end || '17:00'}
                    onChange={(e) => setScheduleForm({
                      ...scheduleForm,
                      [day]: { ...scheduleForm[day], end: e.target.value }
                    })}
                    className="w-24"
                  />
                </div>
              );
            })}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScheduleModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveSchedule} disabled={savingSchedule}>
              {savingSchedule && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default AgencyManifestPage;
